import React, { useEffect, useState } from 'react';

import { makeStyles } from '@material-ui/styles';
import { Theme, Card, CardContent, Typography, Container, Grid, Button } from '@material-ui/core';

import {
    find as _find,
    filter as _filter,
    get as _get,
    keys as _keys,
    capitalize as _capitalize,
    cloneDeep as _cloneDeep,
    trimEnd as _trimEnd,
    size,
  } from "lodash";

import Page from '../../components/page';
import YouTube from 'react-youtube';
import ExportDataForm from '../facilities/components/forms/export-data-form';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { FacilityActionType } from '../../store/facilities/facilities-action-types';
import { Picker } from '@material-ui/pickers';
import { type } from 'os';
import { Navigations } from '../../settings/nav-config';
import AtDatePicker from '../../components/form/date-picker';
import CircularIndeterminate from '../../components/circular-indeterminate';
import { UserRoles } from '../../settings/user-roles';
import { toast } from 'react-toastify';
import TextField from '@material-ui/core/TextField';
import { client, APIURL } from '../../services/at-app-api';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3)
  },
  promisCard: {
    marginTop: theme.spacing(3),
  },
  careSourceCard: {
    marginTop: theme.spacing(3),
  },
  careSourceFormHeader: {
    width: '100%',
    padding: theme.spacing(1),
    color: '#2962FE',
    fontSize: '20px',
  },
  previewBox: {
    background: theme.palette.grey[100],
    borderRadius: 4,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  previewRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  previewLabel: {
    fontWeight: 600,
    minWidth: 160,
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  previewValue: {
    fontSize: 13,
  },
  promisFormHeader: {
    width: '100%',
    padding: theme.spacing(1),
    color: '#2962FE',
    fontSize: '20px',
  },
  promisMsg: {
    fontSize: '12px',
    padding: theme.spacing(2),
  },
  buttonContainer: {
    textAlign: 'right',
    paddingTop: theme.spacing(2),
  },
  submitButton: {
    width: 100,
    padding: theme.spacing(1),
  },
  grid: {
    marginTop: theme.spacing(2)
  },
  videoPlayer: {
    maxWidth: 400,
    height: '280px',
    padding: '10px'
  },
  resourceCard: {
    margin: '15px', 
    display: 'inline-grid' ,
    minWidth: '450px',
    height: '340px'
  },
  videoBox: {
    display: 'inline-box',
    paddingBottom: '20px',
  },
  resourceLink: {
    padding: '20px',
    textAlign: 'center'
  },
  resourceTitle: {
    fontSize: '14px',
    paddingBottom: '10px',
    color: '#546e7a'
  },
  resourceCert: {
    padding: '10px',
    alignContent: 'middle'
  }
}));

interface CareSourceUserDetail {
  careSourceId: string
  firstName: string
  lastName: string
  primaryPhone: string
  cellPhoneNumber: string
  primaryAddressZipcode: string
  primaryCcName: string
  primaryCcEmail: string
  subscriptionCode: string
  assignmentDate: string
  processStatus: string
  endUserUid: string | null
  appUserEmail: string
  importFileName: string
}

const ExportDataDefault = (props: any) => {
  const {
    type,
  } = props;

const dispatch = useDispatch();
const [clinicOptions, setClinicOptions] = useState([] as any);
const [promisStartDate, setPromisStartDate] = useState<Date | null>(null);
const [promisEndDate, setPromisEndDate] = useState<Date | null>(null);
const [isPromisLoading, setIsPromisLoading] = useState(false);

// CareSource notification resend state
const [csSearchId, setCsSearchId] = useState('');
const [csSearching, setCsSearching] = useState(false);
const [csUser, setCsUser] = useState<CareSourceUserDetail | null>(null);
const [csNotFound, setCsNotFound] = useState(false);
const [csCcEmail, setCsCcEmail] = useState('');
const [csResending, setCsResending] = useState(false);

const handleCsSearch = async () => {
  if (!csSearchId.trim()) return;
  setCsSearching(true);
  setCsUser(null);
  setCsNotFound(false);
  try {
    const res = await client().get(`${APIURL}/api/scheduler/caresource_user_lookup?careSourceId=${encodeURIComponent(csSearchId.trim())}`);
    const data: CareSourceUserDetail = res.data;
    setCsUser(data);
    setCsCcEmail(data.appUserEmail || '');
  } catch (err) {
    const e = err as any;
    if (e?.response?.status === 404) {
      setCsNotFound(true);
    } else {
      toast.error(`Lookup failed: ${e?.response?.data?.error || e?.message || 'Unknown error'}`);
    }
  } finally {
    setCsSearching(false);
  }
};

const handleCsResend = async () => {
  if (!csUser?.endUserUid) {
    toast.error('No app user UID available for this referral.');
    return;
  }
  setCsResending(true);
  try {
    await client().post(`${APIURL}/api/scheduler/resend_caresource_welcome`, {
      endUserUid: csUser.endUserUid,
      ccEmail: (csCcEmail && csCcEmail !== csUser.appUserEmail) ? csCcEmail : undefined,
    });
    toast.success(`CareSource welcome re-sent to ${csUser.firstName} ${csUser.lastName}.`);
  } catch (err) {
    const e = err as any;
    toast.error(`Resend failed: ${e?.response?.data?.error || e?.message || 'Unknown error'}`);
  } finally {
    setCsResending(false);
  }
};

const clinics = useSelector(
    (state) => _get(state, ["facilities", "clinics"]) || { data: [], total: 0 },
    shallowEqual
  );
  const userProfile = useSelector(
    (state) => _get(state, ["user", "profile"]) || null,
    shallowEqual
  );

const handleCancel = () => {
    window.history.back();
  };

const loading = false; //needs to be implemented

  const isLoadingPromisExport = useSelector(
    (state: any) => _get(state, ['facilities', 'loadingPromisExport']) || false,
    shallowEqual
  );

  useEffect(() => {
    if (!isLoadingPromisExport && isPromisLoading) {
      setIsPromisLoading(false);
      toast.success('Your PROMIS data has been exported successfully!');
    }
  }, [isLoadingPromisExport]);

  const handlePromisDownload = () => {
    setIsPromisLoading(true);

    // End date: advance to end-of-local-day so the full selected day is included.
    let endDateUtc: string | null = null;
    if (promisEndDate) {
      const endOfDay = new Date(
        promisEndDate.getFullYear(),
        promisEndDate.getMonth(),
        promisEndDate.getDate(),
        23, 59, 59, 999
      );
      endDateUtc = endOfDay.toISOString();
    }

    dispatch({
      type: FacilityActionType.GetPromisExport,
      payload: {
        startDate: promisStartDate ? promisStartDate.toISOString() : null,
        endDate: endDateUtc,
      },
    });
  };

  useEffect(() => {

    dispatch({
      type: FacilityActionType.GetFacilities,
      payload: {
        type: type,
        subType: Navigations.facilities.clinics,
        targetType: Navigations.facilities.clinics,
        facilityUid: null,
        parentFacilityUid: null,
        parentUid: null,
        skip: 0,
        take: 0
      }
    });

    return () => {
    };
  }, []);


  useEffect(() => {

    const temp: any = [];
    const filteredData = clinics.data;
    for (const item of filteredData) {
      temp.push({ value: item.uid, label: item.name });
    }
    setClinicOptions(temp);

    return () => {};
  }, [clinics]);
  

  const classes = useStyles();

  return (
    <Page
      className={classes.root}
      title="Export Data"
    >
      <Container maxWidth={false}>

        <ExportDataForm
        {...{
            type,
            loading,
            handleCancel,
            userProfile, 
          clinicOptions}
        }/>

        {[UserRoles.SUPERUSER, UserRoles.COMPANYADMIN].indexOf(_get(userProfile, 'role')) >= 0 && (
          <Card className={classes.promisCard}>
            <CardContent>
              <div className={classes.promisFormHeader}>PROMIS Data Export</div>
              {isPromisLoading && <CircularIndeterminate />}
              {isPromisLoading && (
                <div className={classes.promisMsg}>
                  Please be patient while your data export is prepared...
                </div>
              )}
              <Grid container direction="row" justifyContent="flex-start" spacing={4}>
                <Grid item xs={12} sm={6}>
                  <Grid container spacing={4}>
                    <Grid item xs={12} sm={6}>
                      <AtDatePicker
                        label="Start Date"
                        value={promisStartDate}
                        onChange={(date: Date | null) => setPromisStartDate(date)}
                        fullWidth
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <AtDatePicker
                        label="End Date"
                        value={promisEndDate}
                        onChange={(date: Date | null) => setPromisEndDate(date)}
                        fullWidth
                        margin="normal"
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <div className={classes.buttonContainer}>
                    <Button
                      className={classes.submitButton}
                      color="secondary"
                      size="medium"
                      variant="contained"
                      disabled={isPromisLoading}
                      onClick={handlePromisDownload}
                    >
                      Download
                    </Button>
                  </div>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {_get(userProfile, 'role') === UserRoles.SUPERUSER && (
          <Card className={classes.careSourceCard}>
            <CardContent>
              <div className={classes.careSourceFormHeader}>CareSource Notification Resend</div>
              <Grid container direction="row" justifyContent="flex-start" spacing={2} style={{ marginTop: 4 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="CareSource Member ID"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={csSearchId}
                    onChange={(e) => { setCsSearchId(e.target.value); setCsUser(null); setCsNotFound(false); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCsSearch(); }}
                    disabled={csSearching}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCsSearch}
                    disabled={csSearching || !csSearchId.trim()}
                    style={{ height: 40 }}
                  >
                    {csSearching ? 'Searching…' : 'Search'}
                  </Button>
                </Grid>
              </Grid>

              {csNotFound && (
                <Typography style={{ marginTop: 12, color: '#c62828', fontSize: 13 }}>
                  No assigned CareSource referral found for that ID.
                </Typography>
              )}

              {csUser && (
                <div className={classes.previewBox}>
                  <Typography style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>
                    Member Preview
                  </Typography>
                  {([
                    ['Name', `${csUser.firstName || ''} ${csUser.lastName || ''}`.trim()],
                    ['CareSource ID', csUser.careSourceId],
                    ['Subscription Code', csUser.subscriptionCode ? 'CSXX-XXXX-XXXX' : '—'],
                    ['Assignment Date', csUser.assignmentDate],
                    ['App Email', csUser.appUserEmail || '—'],
                    ['Primary Phone', csUser.primaryPhone || '—'],
                    ['Cell Phone', csUser.cellPhoneNumber || '—'],
                    ['Zipcode', csUser.primaryAddressZipcode || '—'],
                    ['Care Coordinator', csUser.primaryCcName || '—'],
                    ['Import File', csUser.importFileName || '—'],
                  ] as [string, string][]).map(([label, value]) => (
                    <div className={classes.previewRow} key={label}>
                      <span className={classes.previewLabel}>{label}:</span>
                      <span className={classes.previewValue}>{value}</span>
                    </div>
                  ))}

                  <Grid container direction="row" spacing={2} style={{ marginTop: 12 }} alignItems="flex-start">
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="CC Email"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={csCcEmail}
                        onChange={(e) => setCsCcEmail(e.target.value)}
                        disabled={csResending}
                      />
                      <Typography style={{ fontSize: 11, color: '#546e7a', marginTop: 4 }}>
                        Leave as-is to send to original only, or enter a different address to also CC it.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleCsResend}
                        disabled={csResending || !csUser.endUserUid}
                        style={{ height: 40 }}
                      >
                        {csResending ? 'Sending…' : 'Resend'}
                      </Button>
                    </Grid>
                    {!csUser.endUserUid && (
                      <Grid item xs={12}>
                        <Typography style={{ color: '#c62828', fontSize: 12 }}>
                          No app user account linked to this referral — resend is unavailable.
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </div>
              )}
            </CardContent>
          </Card>
        )}

    </Container>
  </Page>
  );
}

export default ExportDataDefault;

